import {
  closeNotification,
  createNotificationWithPrintListener,
  dismissNotification,
  NotificationOptions,
  NotificationStrings
} from './genericNotification';
import { NOTIFICATION_CONFIG, NotificationType } from './notificationConstants';

interface RecordNotificationConfig {
  notificationKey: string;
  classNamePrefix: string;
  strings: NotificationStrings;
}

type NotificationActionKey = Exclude<keyof NotificationOptions, 'onClose'>;

const createRecordNotification = <
  Type extends NotificationType,
  Actions extends NotificationActionKey = NotificationActionKey
>({
  notificationKey,
  classNamePrefix,
  strings
}: RecordNotificationConfig) => {
  let savedTimerHandle: ReturnType<typeof setTimeout> | null = null;

  const dismiss = () => {
    if (savedTimerHandle !== null) {
      clearTimeout(savedTimerHandle);
      savedTimerHandle = null;
    }
    dismissNotification(classNamePrefix);
  };

  const resumeSavedNotificationTimer = () => {
    if (savedTimerHandle !== null) {
      clearTimeout(savedTimerHandle);
    }
    savedTimerHandle = setTimeout(() => {
      savedTimerHandle = null;
      closeNotification(classNamePrefix);
    }, NOTIFICATION_CONFIG.DURATION * 1000);
  };

  const notify = (
    type: Type,
    onClose: () => void = () => {},
    options?: Pick<NotificationOptions, Actions>
  ) => {
    const actions = options as Omit<NotificationOptions, 'onClose'> | undefined;
    return createNotificationWithPrintListener(
      type,
      strings,
      notificationKey,
      classNamePrefix,
      {
        onDuplicate: actions?.onDuplicate,
        onPrint: actions?.onPrint,
        onRetry: actions?.onRetry,
        onViewWorkOrder: actions?.onViewWorkOrder,
        onClose
      }
    );
  };

  return { notify, dismiss, resumeSavedNotificationTimer };
};

export default createRecordNotification;
